import { cashActionErrorToastKey } from '@/api/cash-management';
import { api } from '@/api/client';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { cn, downloadAsFile, objectsToCsv } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Download, Mail, RefreshCw, Undo2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  REMINDER_THRESHOLDS,
  SUSPENSION_THRESHOLD_DAYS,
  buildAgingView,
  getReminderBadgeClasses,
  getReminderLevelForBucket,
  getReminderLevelInfo,
  type AgingPartnerView,
  type AgingReportDto,
} from './receivables-utils';

/**
 * Story 46-6: Kintlévőség & Pénzügy oldalak
 * Öregedési riport (30/60/90 napos aging report)
 *
 * T-71-24 (a): a válasz a BE `AgingReportData` alakja; a késedelmes összeget/arányt és a
 * sávonkénti partner-bontást a `buildAgingView` képezi. ⚠️ A BE partner-státuszt NEM ad,
 * ezért a „felfüggesztve” jelölés és a visszavonás-gomb valós adaton nem jelenik meg.
 */

interface AgingApiResponse {
  data: AgingReportDto;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function ReceivablesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [actionFeedback, setActionFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const showFeedback = useCallback((type: 'success' | 'error', message: string) => {
    clearTimeout(feedbackTimeoutRef.current);
    setActionFeedback({ type, message });
    feedbackTimeoutRef.current = setTimeout(() => setActionFeedback(null), 3000);
  }, []);

  const {
    data: agingData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<AgingApiResponse>({
    queryKey: ['receivables-aging', 'full'],
    // 47Y route-fix: a `/finance/receivables/aging` route sosem létezett (nincs FinanceModule
    // a BE-ben) — a helyes, ténylegesen bekötött route a dashboard-modulban él (lásd
    // ReceivablesAgingWidget.tsx, ami már ezt hívja helyesen).
    queryFn: () => api.get('/dashboard/receivables/aging'),
    staleTime: 300_000, // 5 minutes
  });

  // Story 46-8: Partner suspension mutation
  const suspendMutation = useMutation({
    mutationFn: async ({ partnerId, suspend }: { partnerId: string; suspend: boolean }) => {
      return api.patch(`/partners-direct/${partnerId}`, {
        status: suspend ? 'BLACKLISTED' : 'ACTIVE',
      });
    },
    onSuccess: (_, { suspend, partnerId }) => {
      showFeedback(
        'success',
        suspend
          ? t('finance.partnerSuspended', { id: partnerId })
          : t('finance.partnerUnsuspended', { id: partnerId })
      );
      queryClient.invalidateQueries({ queryKey: ['receivables-aging'] });
    },
    onError: (err: unknown) => {
      showFeedback('error', t(cashActionErrorToastKey(err)));
    },
  });

  // Story 46-8: Send reminder handler with escalation level
  const handleSendReminder = useCallback(
    (partner: AgingPartnerView, bucketMinDays: number) => {
      const level = getReminderLevelForBucket(bucketMinDays);
      const levelInfo = level ? getReminderLevelInfo(level) : null;
      const levelLabel = levelInfo ? ` (${levelInfo.label})` : '';
      // TODO: Replace with actual API call when backend endpoint is available:
      // api.post(`/partners/${partner.partnerId}/remind`, { level })
      showFeedback(
        'success',
        t('finance.reminderSent', { level: levelLabel, name: partner.partnerName })
      );
    },
    [showFeedback, t]
  );

  const handleSuspend = useCallback(
    (partnerId: string, partnerName: string) => {
      if (window.confirm(t('finance.confirmSuspend', { name: partnerName }))) {
        suspendMutation.mutate({ partnerId, suspend: true });
      }
    },
    [suspendMutation, t]
  );

  const handleUnsuspend = useCallback(
    (partnerId: string) => {
      if (window.confirm(t('finance.confirmUnsuspend'))) {
        suspendMutation.mutate({ partnerId, suspend: false });
      }
    },
    [suspendMutation, t]
  );

  // Story 46-7: Export CSV functionality — must be before early returns (Rules of Hooks)
  const handleExportCsv = useCallback(() => {
    const report = agingData?.data;
    if (!report) return;
    const exportRows = buildAgingView(report).buckets.flatMap(bucket =>
      bucket.partners.map(partner => ({
        oregedesiSav: bucket.label,
        partnerNev: partner.partnerName,
        partnerId: partner.partnerId,
        osszeg: partner.amount,
        szamlakSzama: partner.invoiceCount,
        legregebbiSzamla: partner.oldestDueDate,
      }))
    );

    const csv = objectsToCsv(exportRows, [
      { key: 'oregedesiSav', header: t('finance.csvHeaderAgingBucket') },
      { key: 'partnerNev', header: t('finance.csvHeaderPartnerName') },
      { key: 'partnerId', header: t('finance.csvHeaderPartnerId') },
      { key: 'osszeg', header: t('finance.csvHeaderAmountHuf') },
      { key: 'szamlakSzama', header: t('finance.csvHeaderInvoiceCount') },
      { key: 'legregebbiSzamla', header: t('finance.csvHeaderOldestInvoice') },
    ]);

    const date = new Date().toISOString().slice(0, 10);
    downloadAsFile(csv, `kintlevosegek-oregedesi-riport-${date}.csv`);
  }, [agingData, t]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen kgc-bg">
        <header className="bg-white dark:bg-[#1a4f5c] shadow-sm">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                {t('common.back')}
              </Button>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('finance.receivables')}
              </h1>
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error || !agingData?.data) {
    return (
      <div className="min-h-screen kgc-bg">
        <header className="bg-white dark:bg-[#1a4f5c] shadow-sm">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                {t('common.back')}
              </Button>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('finance.receivables')}
              </h1>
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-4">{t('finance.loadingError')}</p>
              <Button onClick={() => refetch()}>{t('common.retry')}</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const aging = buildAgingView(agingData.data);

  return (
    <div className="min-h-screen kgc-bg">
      {/* Story 46-8: Action feedback toast */}
      {actionFeedback && (
        <div
          role="status"
          aria-live="polite"
          className={cn(
            'fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-2',
            actionFeedback.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-300'
              : 'bg-red-100 text-red-800 border border-red-300'
          )}
        >
          {actionFeedback.type === 'success' ? (
            <Mail className="h-5 w-5" />
          ) : (
            <AlertTriangle className="h-5 w-5" />
          )}
          {actionFeedback.message}
        </div>
      )}

      {/* Header */}
      <header className="bg-white dark:bg-[#1a4f5c] shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('finance.receivablesAgingTitle')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('finance.generatedAt', { date: formatDate(aging.generatedAt) })}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={cn('h-4 w-4 mr-2', isFetching && 'animate-spin')} />
              {t('finance.refresh')}
            </Button>
            <Button variant="outline" onClick={handleExportCsv}>
              <Download className="h-4 w-4 mr-2" />
              {t('finance.exportCsv')}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Summary cards */}
        <div className="grid gap-6 md:grid-cols-3 mb-8">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {t('finance.totalReceivables')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(aging.totalReceivables)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {t('finance.overdueReceivables')}
              </p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(aging.overdueReceivables)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {t('finance.overdueRatio')}
              </p>
              <p
                className={cn(
                  'text-2xl font-bold',
                  aging.overduePercentage > 30
                    ? 'text-red-600'
                    : aging.overduePercentage > 15
                      ? 'text-orange-500'
                      : 'text-green-600'
                )}
              >
                {aging.overduePercentage.toFixed(1)}%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Aging buckets */}
        <Card>
          <CardHeader>
            <CardTitle>{t('finance.agingBuckets')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {aging.buckets.map((bucket, index) => (
                <div
                  key={bucket.label}
                  className={cn(
                    'rounded-lg border p-4',
                    index === 0 &&
                      'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20',
                    index === 1 &&
                      'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20',
                    index === 2 &&
                      'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20',
                    index >= 3 && 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                  )}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {bucket.label}
                        </h4>
                        {/* Story 46-8: Reminder level badge */}
                        {(() => {
                          const level = getReminderLevelForBucket(bucket.minDays);
                          if (!level) return null;
                          const info = getReminderLevelInfo(level);
                          return (
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border',
                                getReminderBadgeClasses(level)
                              )}
                              title={info.description}
                              data-testid={`reminder-level-${level.toLowerCase()}`}
                            >
                              <Mail className="h-3 w-3" />
                              {info.label}
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('finance.partnerCount', {
                          count: bucket.count,
                          invoices: bucket.partners.reduce((sum, p) => sum + p.invoiceCount, 0),
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={cn(
                          'text-xl font-bold',
                          index === 0 && 'text-green-600',
                          index === 1 && 'text-yellow-600',
                          index === 2 && 'text-orange-600',
                          index >= 3 && 'text-red-600'
                        )}
                      >
                        {formatCurrency(bucket.totalAmount)}
                      </p>
                    </div>
                  </div>

                  {/* Partner list */}
                  {bucket.partners.length > 0 && (
                    <div className="space-y-2">
                      {bucket.partners.slice(0, 5).map(partner => {
                        const isSuspended = partner.status === 'BLACKLISTED';
                        // ^ a BE ma nem ad státuszt ⇒ valós adaton mindig `false` (T-71-24 a).
                        const showReminder = bucket.minDays >= REMINDER_THRESHOLDS.FIRST;
                        const showSuspend = bucket.minDays >= SUSPENSION_THRESHOLD_DAYS;

                        return (
                          <div
                            key={partner.partnerId}
                            className={cn(
                              'flex items-center justify-between py-2 px-3 rounded',
                              isSuspended
                                ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700'
                                : 'bg-white/50 dark:bg-gray-800/50'
                            )}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {partner.partnerName}
                                </p>
                                {isSuspended && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-600 text-white rounded-full">
                                    <Ban className="h-3 w-3" />
                                    {t('finance.suspended')}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {t('finance.invoicesOldest', {
                                  count: partner.invoiceCount,
                                  date: formatDate(partner.oldestDueDate),
                                })}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold mr-2">{formatCurrency(partner.amount)}</p>
                              {/* Story 46-8: Action buttons */}
                              {showReminder && !isSuspended && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSendReminder(partner, bucket.minDays)}
                                  title={(() => {
                                    const level = getReminderLevelForBucket(bucket.minDays);
                                    const info = level ? getReminderLevelInfo(level) : null;
                                    return info
                                      ? t('finance.sendReminderTitle', { level: info.label })
                                      : t('finance.sendReminderDefault');
                                  })()}
                                >
                                  <Mail className="h-4 w-4" />
                                </Button>
                              )}
                              {showSuspend && !isSuspended && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleSuspend(partner.partnerId, partner.partnerName)
                                  }
                                  title={t('finance.suspendPartnerTitle')}
                                  className="text-red-600 dark:text-red-400 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                >
                                  <Ban className="h-4 w-4" />
                                </Button>
                              )}
                              {isSuspended && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUnsuspend(partner.partnerId)}
                                  title={t('finance.unsuspendPartnerTitle')}
                                  className="text-green-600 dark:text-green-400 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                                >
                                  <Undo2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {bucket.partners.length > 5 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center pt-2">
                          {t('finance.morePartners', { count: bucket.partners.length - 5 })}
                        </p>
                      )}
                    </div>
                  )}

                  {bucket.partners.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      {t('finance.noReceivablesInBucket')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
